import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Collapse, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import {
  buildRiskDescriptionView,
  getDescriptionFallbackLines,
} from "../../../shared/riskDescriptionDict";
import RiskPieChart from "./RiskPieChart";
import RiskBarChart from "./RiskBarChart";

// Two-column bordered table of label/value rows (the "basic data" look).
function RowsTable({ rows, dense }) {
  if (!rows?.length) return null;
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {rows.map((row, rowIndex) => (
        <Box
          key={`${row.label}-${rowIndex}`}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "stretch",
            borderBottom: rowIndex < rows.length - 1 ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              px: dense ? 0.75 : 1,
              py: dense ? 0.75 : 1,
              textAlign: "left",
              borderRight: "1px solid",
              borderColor: "divider",
            }}
          >
            {row.label}
          </Typography>
          <Typography
            variant={dense ? "body2" : "body1"}
            sx={{
              fontWeight: 500,
              px: dense ? 0.75 : 1,
              py: dense ? 0.75 : 1,
              textAlign: "left",
              fontSize: dense ? "0.85rem" : undefined,
            }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function NoteBox({ text, dense }) {
  if (!text) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        mt: 1.25,
        px: dense ? 1 : 1.25,
        py: dense ? 0.85 : 1,
        borderRadius: 1,
        borderLeft: "3px solid",
        borderColor: "info.main",
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
      }}
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: dense ? "1rem" : "1.15rem",
          color: "info.main",
          mt: "1px",
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: "text.primary",
          fontWeight: 500,
          lineHeight: dense ? 1.45 : 1.55,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// A titled, clickable header that expands/collapses its content.
function CollapsibleSection({ title, open, onToggle, dense, children }) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          px: dense ? 1 : 1.25,
          py: dense ? 0.75 : 1,
          bgcolor: "action.hover",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          {title}
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: "text.secondary",
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ p: dense ? 1 : 1.25 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

// Renders the body of a non-polygon group (table / pie / bar).
function GroupContent({ group, dense }) {
  if (group.type === "table") {
    return <RowsTable rows={group.rows} dense={dense} />;
  }
  if (group.type === "pie") {
    return (
      <RiskPieChart
        slices={group.slices}
        colorMode={group.colorMode}
        riskType={group.riskType}
      />
    );
  }
  if (group.type === "bar") {
    return (
      <>
        <RiskBarChart
          bars={group.bars}
          xLabel={group.xLabel}
          yLabel={group.yLabel}
        />
        <NoteBox text={group.note} dense={dense} />
      </>
    );
  }
  return null;
}

// Flatten every collapsible key (including nested per-polygon children) so the
// expand-all toggle can open/close the whole tree at once.
const collectKeys = (groups) => {
  const keys = [];
  groups.forEach((group) => {
    keys.push(group.key);
    if (group.type === "polygon") {
      group.groups.forEach((child) => keys.push(`${group.key}/${child.key}`));
    }
  });
  return keys;
};

function RiskDescriptionContent({
  riskType,
  description,
  descriptionDict,
  risk,
  dense = false,
}) {
  const { t, i18n } = useTranslation();

  const view = useMemo(
    () =>
      buildRiskDescriptionView({
        riskType,
        descriptionDict,
        risk,
        locale: i18n.resolvedLanguage || i18n.language,
        translate: t,
      }),
    [descriptionDict, risk, i18n.language, i18n.resolvedLanguage, riskType, t],
  );

  const allKeys = useMemo(
    () => (view ? collectKeys(view.groups) : []),
    [view],
  );
  // Everything starts collapsed except the basic-data section, which stays open
  // on point assessments and is collapsed only for area assessments.
  const [openKeys, setOpenKeys] = useState(
    () => new Set(view && !view.isArea ? ["basicData"] : []),
  );

  const toggleKey = (key) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const allExpanded =
    allKeys.length > 0 && allKeys.every((key) => openKeys.has(key));
  const toggleAll = () =>
    setOpenKeys(allExpanded ? new Set() : new Set(allKeys));

  if (!view) {
    const lines = getDescriptionFallbackLines(description);
    if (!lines.length) return null;

    return (
      <Typography
        variant={dense ? "body2" : "body1"}
        sx={{
          color: "text.secondary",
          lineHeight: dense ? 1.45 : 1.55,
          fontSize: dense ? "0.85rem" : undefined,
        }}
      >
        {lines.map((line, index) => (
          <span key={`${line}-${index}`}>
            {line}
            {index < lines.length - 1 && <br />}
          </span>
        ))}
      </Typography>
    );
  }

  const { groups } = view;

  return (
    <Stack spacing={dense ? 1.5 : 2}>
      {groups.length ? (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={toggleAll}
              startIcon={
                allExpanded ? (
                  <UnfoldLessIcon fontSize="small" />
                ) : (
                  <UnfoldMoreIcon fontSize="small" />
                )
              }
              sx={{ textTransform: "none" }}
            >
              {allExpanded
                ? t("descriptionDict.actions.collapseAll", "Collapse all")
                : t("descriptionDict.actions.expandAll", "Expand all")}
            </Button>
          </Box>

          <Stack spacing={dense ? 1 : 1.25}>
            {groups.map((group) => (
              <CollapsibleSection
                key={group.key}
                title={group.title}
                open={openKeys.has(group.key)}
                onToggle={() => toggleKey(group.key)}
                dense={dense}
              >
                {group.type === "polygon" ? (
                  <Stack spacing={dense ? 1 : 1.25}>
                    <RowsTable rows={group.basicRows} dense={dense} />
                    {group.groups.map((child) => {
                      const childKey = `${group.key}/${child.key}`;
                      return (
                        <CollapsibleSection
                          key={childKey}
                          title={child.title}
                          open={openKeys.has(childKey)}
                          onToggle={() => toggleKey(childKey)}
                          dense={dense}
                        >
                          <GroupContent group={child} dense={dense} />
                        </CollapsibleSection>
                      );
                    })}
                  </Stack>
                ) : (
                  <GroupContent group={group} dense={dense} />
                )}
              </CollapsibleSection>
            ))}
          </Stack>
        </>
      ) : null}
    </Stack>
  );
}

export default RiskDescriptionContent;
